import React, { useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import "bootstrap-daterangepicker/daterangepicker.css";

const HistoryDateFilter = ({ 
    startDate, 
    endDate, 
    searchMode = "date", 
    count = 20, 
    searchText, 
    setSearchText, 
    setStartDate,
    setEndDate,
    handleSearch,
    onApply, 
    showSearchModeSelect = false,
    t 
}) => {
    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);
    const [tempSearchText, setTempSearchText] = useState(searchText || "");
    const [tempSearchMode, setTempSearchMode] = useState(searchMode);
    const [tempCount, setTempCount] = useState(count);

    // State for interactive styles
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [isBtnHovered, setIsBtnHovered] = useState(false);
    const [isBtnActive, setIsBtnActive] = useState(false);
    const [isDateHovered, setIsDateHovered] = useState(false);

    const ranges = {
        [t("range.Today") || "اليوم"]: [moment().toDate(), moment().toDate()],
        [t("range.Yesterday") || "أمس"]: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
        [t("range.Last7Days") || "آخر 7 أيام"]: [moment().subtract(6, 'days'), moment()],
        [t("range.Last30Days") || "آخر 30 يوم"]: [moment().subtract(29, 'days'), moment()],
        [t("range.ThisMonth") || "هذا الشهر"]: [moment().startOf('month'), moment().endOf('month')],
        [t("range.LastMonth") || "الشهر الماضي"]: [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
    };

    const handleDateChange = (start, end) => {
        setTempStartDate(start);
        setTempEndDate(end);
    };

    const handleSearchClick = () => {
        if (onApply) {
            onApply(tempSearchMode, tempStartDate, tempEndDate, tempCount, tempSearchText);
        } else {
            if (setStartDate) setStartDate(tempStartDate);
            if (setEndDate) setEndDate(tempEndDate);
            if (setSearchText) setSearchText(tempSearchText);
            if (handleSearch) handleSearch();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearchClick();
        }
    };

    const dateLabel = `${tempStartDate.format("DD/MM/YYYY")} - ${tempEndDate.format("DD/MM/YYYY")}`;

    // Modern glassmorphism style container
    const containerStyle = {
        direction: 'rtl',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.95))',
        borderRadius: '16px',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: '0 10px 25px -5px rgba(146, 39, 143, 0.05), 0 8px 10px -6px rgba(146, 39, 143, 0.03)',
        padding: '16px 20px',
        margin: '0 0 24px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        backdropFilter: 'blur(8px)'
    };

    const inputGroupStyle = {
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        height: '42px',
        boxSizing: 'border-box',
        flex: '1',
        minWidth: '280px',
        maxWidth: '450px',
        borderRadius: '12px',
        border: isSearchFocused ? '1px solid #92278f' : '1px solid #cbd5e1',
        backgroundColor: isSearchFocused ? '#ffffff' : '#f8fafc',
        boxShadow: isSearchFocused 
            ? '0 0 0 3px rgba(146, 39, 143, 0.12)' 
            : 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
        overflow: 'hidden',
        transition: 'all 0.25s ease'
    };

    const searchInputStyle = {
        flex: '1',
        height: '100%',
        padding: '0 40px 0 14px',
        border: 'none',
        background: 'transparent',
        fontSize: '0.9rem',
        outline: 'none',
        textAlign: 'right',
        color: '#1e293b'
    };

    const searchIconStyle = {
        position: 'absolute',
        right: '14px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: isSearchFocused ? '#92278f' : '#94a3b8',
        fontSize: '1rem',
        pointerEvents: 'none',
        transition: 'color 0.25s ease',
        zIndex: '2'
    };

    const dateBtnStyle = {
        height: '42px',
        boxSizing: 'border-box',
        borderRadius: '12px',
        border: isDateHovered ? '1px solid #92278f' : '1px solid #cbd5e1',
        color: '#1e293b',
        backgroundColor: '#ffffff',
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'all 0.25s ease',
        cursor: 'pointer',
        boxShadow: isDateHovered ? '0 4px 12px rgba(146, 39, 143, 0.05)' : 'none'
    };

    const btnStyle = {
        background: 'linear-gradient(135deg, #a83ab5, #92278f)',
        color: '#ffffff',
        border: 'none',
        borderRadius: '0',
        height: '100%',
        padding: '0 24px',
        fontWeight: '600',
        fontSize: '0.9rem',
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: 'auto',
        opacity: isBtnHovered ? '0.95' : '1',
        transform: isBtnActive ? 'scale(0.98)' : 'none'
    };

    const selectStyle = {
        height: '42px',
        boxSizing: 'border-box',
        borderRadius: '12px',
        border: '1px solid #cbd5e1',
        backgroundColor: '#ffffff',
        padding: '0 16px',
        fontSize: '0.85rem',
        fontWeight: '500',
        color: '#1e293b',
        outline: 'none',
        cursor: 'pointer',
        transition: 'all 0.25s ease'
    };

    return (
        <div style={containerStyle}>
            {/* Right side: Search Mode + Date / Count Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                {/* Search Mode Select */}
                {showSearchModeSelect && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#334155' }}>
                            {t("HistoryFilter.SearchMode") || "طريقة البحث"}:
                        </span>
                        <select
                            value={tempSearchMode}
                            onChange={(e) => setTempSearchMode(e.target.value)}
                            style={selectStyle}
                        >
                            <option value="date">{t("HistoryFilter.ByDate") || "بالتاريخ"}</option>
                            <option value="count">{t("HistoryFilter.ByLastTransactions") || "آخر العمليات"}</option>
                        </select>
                    </div>
                )}

                {/* Conditional Field: DateRangePicker or Count Input */}
                {tempSearchMode === "date" ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DateRangePicker
                            initialSettings={{
                                startDate: tempStartDate.toDate(),
                                endDate: tempEndDate.toDate(),
                                ranges: ranges,
                                showDropdowns: true,
                                maxDate: moment(),
                                locale: {
                                    direction: 'rtl',
                                    format: 'DD/MM/YYYY',
                                    separator: ' - ',
                                    applyLabel: t('Apply') || 'إعتماد',
                                    cancelLabel: t('Cancel') || 'إلغاء',
                                    fromLabel: 'من',
                                    toLabel: 'إلى',
                                    customRangeLabel: t('range.customRange') || 'مخصص',
                                    daysOfWeek: [
                                        t("daysOfWeek.Sunday") || 'ح',
                                        t("daysOfWeek.Monday") || 'ن',
                                        t("daysOfWeek.Tuesday") || 'ث',
                                        t("daysOfWeek.Wednesday") || 'ر',
                                        t("daysOfWeek.Thursday") || 'خ',
                                        t("daysOfWeek.Friday") || 'ج',
                                        t("daysOfWeek.Saturday") || 'س'
                                    ],
                                    monthNames: [
                                        t("months.January") || 'يناير',
                                        t("months.February") || 'فبراير',
                                        t("months.March") || 'مارس',
                                        t("months.April") || 'أبريل',
                                        t("months.May") || 'مايو',
                                        t("months.June") || 'يونيو',
                                        t("months.July") || 'يوليو',
                                        t("months.August") || 'أغسطس',
                                        t("months.September") || 'سبتمبر',
                                        t("months.October") || 'أكتوبَر',
                                        t("months.November") || 'نوفمبر',
                                        t("months.December") || 'ديسمبر'
                                    ]
                                }
                            }}
                            onCallback={handleDateChange}
                        >
                            <button 
                                type="button"
                                style={dateBtnStyle}
                                onMouseEnter={() => setIsDateHovered(true)}
                                onMouseLeave={() => setIsDateHovered(false)}
                            >
                                <i className="fal fa-calendar-alt ml-1" style={{ color: '#92278f' }}></i>
                                <span style={{ fontSize: '0.85rem', fontWeight: '500' }}>{dateLabel}</span>
                            </button>
                        </DateRangePicker>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#334155' }}>
                            {t("HistoryFilter.TransactionCount") || "عدد العمليات"}:
                        </span>
                        <input
                            type="number"
                            min="1"
                            max="500"
                            value={tempCount}
                            onChange={(e) => setTempCount(parseInt(e.target.value) || 1)}
                            style={{
                                ...selectStyle,
                                width: '80px',
                                textAlign: 'center'
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Left side: Search input + Button grouped together */}
            <div style={inputGroupStyle}>
                <i className="fal fa-search" style={searchIconStyle}></i>
                <input
                    type="text"
                    placeholder={t("SearchName.Placeholder") || "بحث حسب الاسم..."}
                    value={tempSearchText}
                    onChange={(e) => setTempSearchText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={searchInputStyle}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                />
                <button 
                    type="button"
                    onClick={handleSearchClick}
                    style={btnStyle}
                    onMouseEnter={() => setIsBtnHovered(true)}
                    onMouseLeave={() => {
                        setIsBtnHovered(false);
                        setIsBtnActive(false);
                    }}
                    onMouseDown={() => setIsBtnActive(true)}
                    onMouseUp={() => setIsBtnActive(false)}
                >
                    <i className="fal fa-filter"></i>
                    <span style={{ whiteSpace: 'nowrap' }}>{t("ControlPanel.Indices.search.title") || "بحث"}</span>
                </button>
            </div>
        </div>
    );
};

export default HistoryDateFilter;
